import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import Appointment from 'model/Appointment'
import { AppThunk } from 'store'
import AppointmentRepository from 'clients/db/AppointmentRepository'
import Patient from 'model/Patient'
import PatientRepository from 'clients/db/PatientRepository'
import { reloadApointments } from './appointments-slice'
import { fetchNavbarData } from 'dashboard/dashboard-slice'
import { getUserDetails } from 'user/UserApi'
import PatientsFilesRepository from 'clients/db/PatientsFilesRepository'
import PatientFile from 'model/PatientFile'

interface AppointmentState {
  appointment: Appointment
  patient: Patient
  isLoading: boolean
  lockedById: string
  lockedByName: string
  appointmentReferrals: any[]
  appointmentRelatedAppointments: Appointment[]
  previousAppointment: Appointment
  appointmentExamination: any[]
  appointmentClinic: PatientFile
}

const { server } = (window as any).config

const initialAppointmentState = {
  appointment: {} as Appointment,
  patient: {} as Patient,
  isLoading: false,
  lockedById: '',
  lockedByName: '',
  appointmentReferrals: [],
  appointmentRelatedAppointments: [],
  previousAppointment: {} as Appointment,
  appointmentExamination: [],
  appointmentClinic: {} as PatientFile,
}

function startLoading(state: AppointmentState) {
  state.isLoading = true
  state.lockedById = ''
  state.lockedByName = ''
  state.appointmentReferrals = []
  state.appointmentExamination = []
}

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState: initialAppointmentState,
  reducers: {
    fetchAppointmentStart: startLoading,
    createAppointmentStart: startLoading,
    updateAppointmentStart: startLoading,
    deleteAppointmentStart: startLoading,
    deleteAppointmentSuccess: (state: AppointmentState) => {
      state.isLoading = false
    },
    fetchAppointmentSuccess: (
      state,
      { payload }: PayloadAction<{ appointment: Appointment; patient: Patient }>,
    ) => {
      payload.appointment.photoUrl = server + `/fetchPatientPhoto/${payload.patient.id}`

      state.isLoading = false
      state.appointment = payload.appointment
      state.patient = payload.patient
    },
    createAppointmentSuccess(state) {
      state.isLoading = false
    },
    updateAppointmentSuccess(state, { payload }: PayloadAction<Appointment>) {
      state.isLoading = false
      state.appointment = payload
    },
    fetchAppointmentSilentlySuccess(state, { payload }: PayloadAction<Appointment>) {
      if (state.appointment.id === payload.id) {
        state.appointment = payload
      }
    },
    lockedBy(
      state,
      { payload }: PayloadAction<{ appointment: Appointment; lockedByName: string }>,
    ) {
      if (state.appointment.id === payload.appointment.id) {
        state.lockedByName = payload.lockedByName
        state.lockedById = payload.appointment.lockedBy
      }
    },
    fetchappointmentReferralsSuccess(state, { payload }: PayloadAction<any>) {
      state.appointmentReferrals = payload.appointmentReferrals
    },

    fetchappointmentRelatedAppointmentsSuccess(state, { payload }: PayloadAction<any>) {
      state.appointmentRelatedAppointments = payload.appointmentRelatedAppointments
    },

    fetchAppointmentPreviousAppointmenSuccess(state, { payload }: PayloadAction<any>) {
      state.previousAppointment = payload.previousAppointment
    },
    fetchappointmentExaminationSuccess(state, { payload }: PayloadAction<any>) {
      state.appointmentExamination = payload.appointementExamination
    },
    fetchappointmentClinicSuccess(state, { payload }: PayloadAction<PatientFile>) {
      state.appointmentClinic = payload
    },
    clearAppointmentSuccess(state) {
      state.appointment = {} as Appointment
    },
  },
})

export const {
  createAppointmentStart,
  createAppointmentSuccess,
  updateAppointmentStart,
  updateAppointmentSuccess,
  fetchAppointmentStart,
  fetchAppointmentSuccess,
  deleteAppointmentStart,
  deleteAppointmentSuccess,
  fetchAppointmentSilentlySuccess,
  lockedBy,
  fetchappointmentReferralsSuccess,
  fetchappointmentRelatedAppointmentsSuccess,
  fetchAppointmentPreviousAppointmenSuccess,
  fetchappointmentExaminationSuccess,
  fetchappointmentClinicSuccess,
  clearAppointmentSuccess,
} = appointmentSlice.actions

export const fetchAppointment = (id: string): AppThunk => async (dispatch) => {
  dispatch(fetchAppointmentStart())
  const appointment = await AppointmentRepository.find(id)
  const patient = await PatientRepository.find(appointment.patientId)

  dispatch(fetchAppointmentSuccess({ appointment, patient }))
}

export const clearAppointment = (): AppThunk => async (dispatch) => {
  dispatch(clearAppointmentSuccess())
}

export const createAppointment = (
  appointment: Appointment,
  onSuccess?: (appointment: Appointment) => void,
): AppThunk => async (dispatch) => {
  dispatch(createAppointmentStart())
  const newAppointment = await AppointmentRepository.save(appointment)
  dispatch(createAppointmentSuccess())
  dispatch(fetchNavbarData())
  if (onSuccess) {
    onSuccess(newAppointment)
  }
  dispatch(reloadApointments())
}

export const updateAppointment = (
  appointment: Appointment,
  onSuccess?: (appointment: Appointment) => void,
): AppThunk => async (dispatch) => {
  dispatch(updateAppointmentStart())
  const updatedAppointment = await AppointmentRepository.saveOrUpdate(appointment)
  dispatch(updateAppointmentSuccess(updatedAppointment))

  if (onSuccess) {
    onSuccess(updatedAppointment)
  }
  dispatch(reloadApointments())
}

export const deleteAppointment = (
  appointment: Appointment,
  onSuccess?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(deleteAppointmentStart())
  await AppointmentRepository.delete(appointment)

  dispatch(deleteAppointmentSuccess())

  if (onSuccess) {
    onSuccess()
  }
  dispatch(reloadApointments())
}

async function updateLockedBy(appointment: Appointment, dispatch: any) {
  const response = await getUserDetails(appointment.lockedBy)
  dispatch(lockedBy({ appointment, lockedByName: response.data.name }))
}

export const fetchAppointmentSilently = (id: string): AppThunk => async (dispatch) => {
  const appointment = await AppointmentRepository.find(id)
  dispatch(fetchAppointmentSilentlySuccess(appointment))
  await updateLockedBy(appointment, dispatch)
}

export const lockAppointment = (
  appointment: Appointment,
  onSuccess?: (appointment: Appointment) => void,
  onError?: (error: any) => void,
): AppThunk => async (dispatch) => {
  try {
    const updateAppointment = await AppointmentRepository.saveOrUpdate(appointment)
    if (onSuccess) {
      onSuccess(updateAppointment)
    }

    await updateLockedBy(updateAppointment, dispatch)
  } catch (error) {
    dispatch(fetchAppointmentSilently(appointment.id))

    if (onError) {
      onError(error)
    }
  }
}

export const fetchappointmentReferrals = (appointmentId: string): AppThunk => async (dispatch) => {
  let appointmentReferrals

  // verifict
  appointmentReferrals = appointmentId
    ? await PatientsFilesRepository.searchAppointmentFiles(appointmentId)
    : []

  dispatch(fetchappointmentReferralsSuccess({ appointmentReferrals }))
}

export const fetchAppointmentRelatedAppointments = (appointmentId: string): AppThunk => async (
  dispatch,
) => {
  let appointmentRelatedAppointments

  appointmentRelatedAppointments = appointmentId
    ? await AppointmentRepository.searchAppointmentRelatedAppointments(appointmentId)
    : []

  dispatch(fetchappointmentRelatedAppointmentsSuccess({ appointmentRelatedAppointments }))
}

export const fetchAppointmentPreviousAppointment = (
  previous_appointmentId: string,
): AppThunk => async (dispatch) => {
  let previousAppointment

  previousAppointment = await AppointmentRepository.find(previous_appointmentId)

  dispatch(fetchAppointmentPreviousAppointmenSuccess({ previousAppointment }))
}
export const fetchappointmentExamination = (appointmentId: string): AppThunk => async (
  dispatch,
) => {
  let appointementExamination

  appointementExamination = appointmentId
    ? await PatientsFilesRepository.searchAppointmentExamination(appointmentId)
    : []

  dispatch(fetchappointmentExaminationSuccess({ appointementExamination }))
}

export const fetchappointmentClinic = (appointmentId: string): AppThunk => async (dispatch) => {
  const appointmentClinic = appointmentId
    ? await PatientsFilesRepository.searchAppointmentClinic(appointmentId)
    : []

  dispatch(fetchappointmentClinicSuccess(appointmentClinic[0]))
}

export default appointmentSlice.reducer
